import { useEffect, useLayoutEffect, useRef } from "react";
import { BACKGROUND_LOCATION_KEY, LAYER_ID_KEY } from "./productPageOverlayContext";

const readHistoryTarget = () => {
  const entry = window.history.state ?? {};
  const state = entry.usr ?? {};
  const key = entry.key ?? "default";
  if (!state[BACKGROUND_LOCATION_KEY]) return { key, layerOpen: false };
  return { key: state[LAYER_ID_KEY] ?? key, layerOpen: true };
};

const showLayer = (key) => {
  const layers = [...document.querySelectorAll(".product-layer[data-layer-key]")];
  const target = key == null ? null : layers.find((layer) => layer.dataset.layerKey === key);
  if (key != null && !target) return false;
  const main = document.querySelector("main");
  if (main) main.hidden = key != null;
  layers.forEach((layer) => {
    layer.hidden = layer !== target;
  });
  return true;
};

export const useLayerScroll = (visibleKey, layerOpen = false) => {
  const positionsRef = useRef(new Map());
  const currentKeyRef = useRef(visibleKey);
  const layerOpenRef = useRef(layerOpen);

  useEffect(() => {
    const save = () => positionsRef.current.set(currentKeyRef.current, window.scrollY);
    window.addEventListener("scroll", save, { passive: true });
    return () => window.removeEventListener("scroll", save);
  }, []);

  useEffect(() => {
    const onPopState = () => {
      const target = readHistoryTarget();
      if (target.key === currentKeyRef.current) return;
      if (!target.layerOpen && !layerOpenRef.current) return;
      if (!showLayer(target.layerOpen ? target.key : null)) return;
      currentKeyRef.current = target.key;
      layerOpenRef.current = target.layerOpen;
      window.scrollTo({ top: positionsRef.current.get(target.key) ?? 0, left: 0, behavior: "instant" });
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  useEffect(() => {
    if (!layerOpen) return undefined;
    const previous = window.history.scrollRestoration;
    window.history.scrollRestoration = "manual";
    return () => {
      window.history.scrollRestoration = previous;
    };
  }, [layerOpen]);

  useLayoutEffect(() => {
    if (currentKeyRef.current === visibleKey) {
      layerOpenRef.current = layerOpen;
      return;
    }
    const layerInvolved = layerOpen || layerOpenRef.current;
    currentKeyRef.current = visibleKey;
    layerOpenRef.current = layerOpen;
    if (!layerInvolved) return;
    window.scrollTo({ top: positionsRef.current.get(visibleKey) ?? 0, left: 0, behavior: "instant" });
  }, [visibleKey, layerOpen]);
};
